import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Auth0Provider } from '@auth0/auth0-react';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import RemoteDesktop from './pages/RemoteDesktop';
import RemoteTerminal from './pages/RemoteTerminal';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import ContractorPortal from './pages/ContractorPortal';
import ContractorTerminal from './pages/ContractorTerminal';
import ContractorDesktop from './pages/ContractorDesktop';
import { ZeracTheme } from './theme';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const contractorPortal = import.meta.env.VITE_CONTRACTOR_PORTAL === 'true';

function ContractorApp() {
  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    window.history.replaceState({}, '', appState?.returnTo || '/portal');
  };

  return (
    <Auth0Provider
      onRedirectCallback={onRedirectCallback}
      domain={import.meta.env.VITE_AUTH0_DOMAIN ?? ''}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID ?? ''}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/portal`,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE ?? '',
        scope: 'openid profile email offline_access',
      }}
      cacheLocation="localstorage"
      useRefreshTokens
      useRefreshTokensFallback
    >
      <ThemeProvider theme={ZeracTheme}>
        <CssBaseline />
        <Router>
          <div className="App">
            <Routes>
              <Route path="/portal" element={<ContractorPortal />} />
              <Route path="/terminal" element={<ContractorTerminal />} />
              <Route path="/desktop" element={<ContractorDesktop />} />
              <Route path="*" element={<ContractorPortal />} />
            </Routes>
          </div>
        </Router>
      </ThemeProvider>
    </Auth0Provider>
  );
}

function App() {
  if (contractorPortal) {
    return <ContractorApp />;
  }

  return (
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN ?? ''}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID ?? ''}
      authorizationParams={{
        redirect_uri: `${window.location.origin}/portal`,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE ?? '',
      }}
    >
    <AuthProvider>
      <ThemeProvider theme={ZeracTheme}>
        <CssBaseline />
        <Router>
          <div className="App">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/portal" element={<ContractorPortal />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/:tab?"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/desktop/:deviceId"
                element={
                  <ProtectedRoute requiredRole="contractor">
                    <RemoteDesktop />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/terminal/:deviceId"
                element={
                  <ProtectedRoute requiredRole="contractor">
                    <RemoteTerminal />
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<RoleBasedRedirect />} />
              <Route path="*" element={<RoleBasedRedirect />} />
            </Routes>
          </div>
        </Router>
      </ThemeProvider>
    </AuthProvider>
    </Auth0Provider>
  );
}

export default App;
